/**
 * ProfileContext — single source of truth for the enriched user bundle.
 *
 * Every logged-in page needs data from GET /api/users/me (photo token, score,
 * completion %, counts). Before this context each page re-fetched on mount,
 * which made the header flicker whenever the user navigated. This context
 * fetches once on login, re-fetches on demand, and exposes `setBundle` so
 * mutations (PATCH profile, upload photo, verify phone, etc.) can update the
 * header + dashboard synchronously.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';

const ProfileContext = createContext(null);

export const ProfileProvider = ({ children }) => {
  const { api, isAuthenticated } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!isAuthenticated) {
      setBundle(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/users/me');
      setBundle(res.data);
    } catch (_) {
      setBundle(null);
    } finally {
      setLoading(false);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <ProfileContext.Provider value={{ bundle, setBundle, refetch, loading }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used inside ProfileProvider');
  return ctx;
};
